/**
 * Eric, 2026-10-30: "Put the old icons back exactly." The map sidebar, hover card and unit detail
 * header draw their original battery pill and Sun / Zap / Wind / Wifi icons, with the states taken
 * from the server's live_status (docs/contracts/unit-live-status.md) rather than raw device fields:
 *   - the pill shows the reading's % and App's battery band (healthy green, warning amber,
 *     critical red); no reading shows "—" and an empty pill, never a made-up 0%;
 *   - Sun lit while charging from solar or while the panels produce ("sun = lit up if
 *     charging/receiving sun"); on shore power the Alerts board's plug takes its spot ("yes plug");
 *     Zap while the EV charges, Wind while HVAC is on, Wifi while Starlink is online;
 *   - a subtle ▲ (charging) / ▼ (draining) mark beside the % from battery.direction.
 */
import { batteryCriticalLabel, type UnitLiveStatus } from '@/lib/unitLiveStatus';

export interface LegacyIconState {
  /** The battery % shown, or null for no reading ("—"). */
  batteryLevel: number | null;
  /** Eric 2026-11-04 "15% or less is critical": show the coral Critical label beside the %. */
  critical: boolean;
  /** The pill's fill class from App's band, or null for no reading. */
  batteryFill: 'bg-success' | 'bg-warning' | 'bg-error' | null;
  /** The ▲/▼ mark beside the %: the server's battery.direction when charging or draining, else null. */
  direction: 'charging' | 'draining' | null;
  /** Lit while charging from solar or while the panels produce. */
  solarOn: boolean;
  /** The panels' output (or, from an older server, the solar charging power) for the tooltip, in kW. */
  solarKw: number | null;
  /** On shore power the plug takes the sun's spot: amber like the lit Sun when charging from shore, dimmed when probable. */
  plug: 'shore' | 'shore_likely' | null;
  chargerOn: boolean;
  /** EV charging load for the tooltip, in kW. */
  chargerKw: number | null;
  hvacOn: boolean;
  starlinkOn: boolean;
}

const FILL = { healthy: 'bg-success', warning: 'bg-warning', critical: 'bg-error' } as const;
const kw = (watts: number | null | undefined) => (typeof watts === 'number' && Number.isFinite(watts) ? watts / 1000 : null);

export function legacyIconState(raw: UnitLiveStatus | null | undefined): LegacyIconState {
  // Contract rule 4: a version this client doesn't know draws nothing.
  const status = raw?.version === 1 ? raw : null;
  const band = status?.battery?.band;
  const soc = status?.battery?.soc;
  const known = typeof soc === 'number' && Number.isFinite(soc) && (band === 'healthy' || band === 'warning' || band === 'critical');
  const source = status?.charge?.source;
  const producing = status?.solar?.producing === true;
  // Eric (2026-10-30): "sun = lit up if charging/receiving sun".
  const solarOn = source === 'solar' || producing;
  // The panels' own output when the server sends it (the old tooltip showed generation);
  // otherwise the watts going into the pack.
  const panelKw = producing ? kw(status?.solar?.power_w) : null;
  const chargerOn = status?.ev?.state === 'charging';
  return {
    batteryLevel: known ? Math.round(soc) : null,
    critical: known && batteryCriticalLabel(status) !== null,
    batteryFill: known ? FILL[band] : null,
    // Eric (2026-10-30, mock approved): a mark only beside a real reading, only for the two moving states.
    direction: known && (status?.battery?.direction === 'charging' || status?.battery?.direction === 'draining')
      ? status.battery.direction
      : null,
    solarOn,
    solarKw: !solarOn ? null : panelKw ?? (source === 'solar' ? kw(status?.charge?.power_w) : null),
    // Eric (2026-10-30): "yes plug". On shore power the plug takes the sun's spot.
    plug: source === 'shore' || source === 'shore_likely' ? source : null,
    chargerOn,
    chargerKw: chargerOn ? kw(status?.ev?.load_w) : null,
    hvacOn: status?.hvac?.state === 'on',
    starlinkOn: status?.starlink?.state === 'online',
  };
}

const TEXT = { 'bg-success': 'text-success', 'bg-warning': 'text-warning', 'bg-error': 'text-error' } as const;

export interface LiveBatteryView {
  /** The battery % to show, or null for "—". */
  level: number | null;
  /** Text colour from App's band, or null (no reading, or no live_status: no colour). */
  text: 'text-success' | 'text-warning' | 'text-error' | null;
  /** Fill colour for a bar or pill, matching `text`. */
  fill: LegacyIconState['batteryFill'];
  /** 15% or less (the server's critical_low): show the coral Critical label beside the number. */
  critical: boolean;
}

/**
 * T-197 slice 2: a battery number and colour for screens that show a plain battery reading
 * (the energy leaderboard, the solar panel, the Records battery tile). With live_status
 * (even one without a reading), the server's reading and App's band win. Without one at
 * all, from an older server or when it could not be read, the screen's own reading shows
 * with no colour: this client never works out a band (contract rule 1). A 0% still reads
 * as no reading.
 */
export function liveBatteryView(raw: UnitLiveStatus | null | undefined, fallbackLevel?: number | null): LiveBatteryView {
  if (raw != null) {
    const { batteryLevel, batteryFill, critical } = legacyIconState(raw);
    return { level: batteryLevel, text: batteryFill ? TEXT[batteryFill] : null, fill: batteryFill, critical };
  }
  const usable = typeof fallbackLevel === 'number' && Number.isFinite(fallbackLevel) && fallbackLevel > 0;
  return { level: usable ? Math.round(fallbackLevel) : null, text: null, fill: null, critical: false };
}
